import React from "react";
import { createBrowserRouter, redirect } from "react-router-dom";
import HeimdallContext from "./HeimdallContext";
import LoggedInPage from "./components/LoggedInPage";
import ErrorPage from "./components/ErrorPage";
import Login from "./components/Login";
import { UserData } from "./context/auth";
import Registration from "./components/Registration";
import EmailVerification from "./components/EmailVerification";
import RequestPasswordReset from "./components/RequestPasswordReset";
import PasswordReset from "./components/PasswordReset";
import AccountEmailsPage, { accountEmailsLoader } from "./components/AccountEmailsPage";
import { FetchFunction } from "./components/FetchProvider";
import AccountAliasesPage, { accountAliasesLoader } from "./components/AccountAliasesPage";
import AccountSessionsPage, { accountSessionsLoader } from "./components/AccountSessionsPage";
import AccountSettingsPage from "./components/AccountSettingsPage";
import UsersPage from "./components/UsersPage";
import ClientsPage from "./components/ClientsPage";
import { crudListLoader, fetchRead } from "./utils/crud";
import LDAPAuthsPage from "./components/LDAPAuthsPage";
import ClientView from "./components/ClientView";
import UserView from "./components/UserView";
import AuditPage from "./components/AuditPage";

function withErrorLogging(loader: (...args: any[]) => Promise<any>) {
    return async (...args: any[]) => {
        try {
            return await loader(...args);
        } catch (error) {
            console.error("Error in loader:", error);
            throw error; // Re-throw to let react-router handle it
        }
    };
}

function createRouter(context: HeimdallContext, userData: UserData, fetch: FetchFunction) {
    return createBrowserRouter(
        [
            {
                path: "/public/login",
                element: <Login />,
            },
            {
                path: "/public/register",
                element: <Registration />,
            },
            {
                path: "/public/email-verification/:token",
                element: <EmailVerification />,
            },
            {
                path: "/public/request-password-reset",
                element: <RequestPasswordReset />,
            },
            {
                path: "/public/password-reset/:token",
                element: <PasswordReset />,
            },
            {
                path: "/public/error",
                element: <ErrorPage type="fromQueryParam" />,
            },
            {
                path: "/",
                element: <LoggedInPage context={context} />,
                errorElement: <ErrorPage type="NOT_FOUND" />,
                children: [
                    {
                        index: true,
                        loader: () => {
                            if (context.isInternalIdenityProvider)
                                return redirect(userData.userId ? "/account/general" : "/account/sessions");
                            // The client list serves the client secrets, so it is operator-only.
                            return redirect(userData.hasPermission("system") ? "/clients/list" : "/account/sessions");
                        },
                    },
                    {
                        path: "/audit/list",
                        loader: withErrorLogging(async ({ request }) => {
                            return await crudListLoader(fetch, request, "audit");
                        }),
                        element: <AuditPage />,
                    },
                    {
                        path: "/account/general",
                        element: <AccountSettingsPage />,
                    },
                    {
                        path: "/account/emails",
                        loader: withErrorLogging(async () => {
                            return await accountEmailsLoader(fetch, userData.userId);
                        }),
                        element: <AccountEmailsPage />,
                    },
                    {
                        path: "/account/aliases",
                        loader: withErrorLogging(async () => {
                            return await accountAliasesLoader(fetch, userData.userId);
                        }),
                        element: <AccountAliasesPage />,
                    },
                    {
                        path: "/account/sessions",
                        loader: withErrorLogging(async () => {
                            return await accountSessionsLoader(fetch);
                        }),
                        element: <AccountSessionsPage />,
                    },

                    {
                        path: "/clients/list",
                        loader: withErrorLogging(async ({ request }) => {
                            return await crudListLoader(fetch, request, "client");
                        }),
                        element: <ClientsPage />,
                    },
                    {
                        path: "/clients/read/:id",
                        loader: withErrorLogging(async ({ params }) => {
                            return await fetchRead(fetch, "client", Number(params.id));
                        }),
                        element: <ClientView />,
                    },
                    {
                        path: "/users/list",
                        loader: withErrorLogging(async ({ request }) => {
                            return await crudListLoader(fetch, request, "user");
                        }),
                        element: <UsersPage />,
                    },
                    {
                        path: "/users/read/:id",
                        loader: withErrorLogging(async ({ params }) => {
                            return await fetchRead(fetch, "user", Number(params.id));
                        }),
                        element: <UserView />,
                    },

                    {
                        path: "/ldap_auths/list",
                        loader: withErrorLogging(async ({ request }) => {
                            return await crudListLoader(fetch, request, "ldap_auth");
                        }),
                        element: <LDAPAuthsPage />,
                    },
                    // ldap_auths does not have read because everything is done with an update dialog
                ],
            },
        ],
        {
            future: {
                v7_relativeSplatPath: true,
            },
        },
    );
}

export default createRouter;
